import { EMAIL_PATTERN, RUSSIA_PHONE_NUMBER_REGEXP } from 'utils/validation'

const EMAIL_RE = new RegExp(EMAIL_PATTERN.source)
const RUSSIA_PHONE_RE = new RegExp(`^(?:${RUSSIA_PHONE_NUMBER_REGEXP.source})$`)

export function isApplicationPhoneValid(value: string): boolean {
  if (!value.trim()) return true
  if (!/^[+\d\s().-]+$/.test(value)) return false
  const digits = value.replace(/\D/g, '')
  return digits.startsWith('7')
    ? RUSSIA_PHONE_RE.test(digits)
    : /^\d{7,15}$/.test(digits)
}

const REVEAL_CONTACT_FIELDS: RevealContactsField[] = [
  'executor_manager_name',
  'executor_manager_phone',
  'executor_manager_email',
]

const CLIENT_CONTACT_FIELDS = [
  'client_contact_name',
  'client_contact_phone',
  'client_contact_email',
] as const

export type ClientContactFieldKey = typeof CLIENT_CONTACT_FIELDS[number]
export type ClientContactFieldErrors = Partial<Record<ClientContactFieldKey, string>>

export function validateRevealContactsForm({ form, t }: ApplicationValidateRevealContactsFormArgs
): RevealContactsFieldErrors {
  const errors: RevealContactsFieldErrors = {}

  if (!form.executor_manager_name.trim()) {
    errors.executor_manager_name = t('common__text__isRequired')
  }
  if (!form.executor_manager_phone.trim()) {
    errors.executor_manager_phone = t('common__text__isRequired')
  } else if (!isApplicationPhoneValid(form.executor_manager_phone)) {
    errors.executor_manager_phone = t('errors__message__incorrectPhoneError')
  }

  const email = form.executor_manager_email.trim()
  if (!email) {
    errors.executor_manager_email = t('common__text__isRequired')
  } else if (!EMAIL_RE.test(email)) {
    errors.executor_manager_email = t('errors__message__incorrectEmail')
  }

  return errors
}

export function validateClientContactsForm({ form, t }: {
  form: ClientContactFormValues
  t: ApplicationValidateRevealContactsFormArgs['t']
}): ClientContactFieldErrors {
  const errors: ClientContactFieldErrors = {}

  if (!form.client_contact_name.trim()) {
    errors.client_contact_name = t('applications__text__acceptProposalContactNameRequired')
  }
  if (!form.client_contact_phone.trim() && !form.client_contact_email.trim()) {
    errors.client_contact_phone = t('applications__text__acceptProposalContactRequired')
  }

  if (!isApplicationPhoneValid(form.client_contact_phone)) {
    errors.client_contact_phone = t('errors__message__incorrectPhoneError')
  }

  const email = form.client_contact_email.trim()
  if (email && !EMAIL_RE.test(email)) {
    errors.client_contact_email = t('errors__message__incorrectEmail')
  }

  return errors
}

function fieldErrorsFromApi(data: unknown, fields: readonly string[]): Record<string, string> {
  if (!data || typeof data !== 'object') {
    return {}
  }
  const errors: Record<string, string> = {}
  for (const field of fields) {
    const message = firstDrfMessage((data as Record<string, unknown>)[field])
    if (message) {
      errors[field] = message
    }
  }
  return errors
}

export function clientContactsFieldErrorsFromApi(data: unknown): ClientContactFieldErrors {
  return fieldErrorsFromApi(data, CLIENT_CONTACT_FIELDS) as ClientContactFieldErrors
}

function firstDrfMessage(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value
  }
  if (Array.isArray(value) && value.length > 0) {
    for (const item of value) {
      const nested = firstDrfMessage(item)
      if (nested) {
        return nested
      }
    }
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    for (const nested of Object.values(value as Record<string, unknown>)) {
      const message = firstDrfMessage(nested)
      if (message) {
        return message
      }
    }
  }
  return null
}

function drfFieldErrorMessages(record: Record<string, unknown>): string[] {
  const messages: string[] = []
  const skipKeys = new Set([ 'detail' ])

  for (const [ key, value ] of Object.entries(record)) {
    if (skipKeys.has(key)) {
      continue
    }
    const message = firstDrfMessage(value)
    if (message) {
      messages.push(message)
    }
  }
  return messages
}

export function revealContactsFieldErrorsFromApi(
  data: unknown,
): RevealContactsFieldErrors {
  return fieldErrorsFromApi(data, REVEAL_CONTACT_FIELDS) as RevealContactsFieldErrors
}

export function formatApplicationActionError({ data, fallback }: ApplicationFormatApplicationActionErrorArgs
): string {
  if (!data || typeof data !== 'object') {
    return fallback
  }
  const record = data as Record<string, unknown>
  const detail = record.detail
  if (typeof detail === 'string' && detail.trim()) {
    return detail
  }
  const detailFromArray = firstDrfMessage(detail)
  if (detailFromArray) {
    return detailFromArray
  }

  const fieldMessages = drfFieldErrorMessages(record)
  if (fieldMessages.length > 0) {
    return fieldMessages.join(' ')
  }
  return fallback
}
